import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";

export default function ApprovalInbox() {
  const [items, setItems] = useState([]);
  const [target, setTarget] = useState(null); // { item, action }
  const [comment, setComment] = useState("");

  const load = async () => {
    const { data } = await api.get("/approvals/pending");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const act = async () => {
    if (!target) return;
    try {
      await api.post(`/approvals/${target.item.entity_type}/${target.item.id}/action`, {
        action: target.action, comment: comment || null,
      });
      toast.success(`${target.action === "APPROVE" ? "Approved" : "Rejected"}`);
      setTarget(null); setComment(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="approval-inbox">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Pending approvals</h1>
        <p className="text-muted-foreground">Documents waiting on your action.</p>
      </div>

      {items.length === 0 ? (
        <div className="surface-card p-12 text-center text-muted-foreground" data-testid="approvals-empty">
          🎉 No pending approvals. Have a coffee.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {items.map((i) => (
            <div key={i.id} className="surface-card p-5 space-y-3" data-testid={`approval-card-${i.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <Badge variant="outline" className="font-mono text-[10px]">{i.entity_type.replace("_", " ")}</Badge>
                  <div className="font-display text-lg font-black mt-2">{i.title || i.number}</div>
                  <div className="text-xs text-muted-foreground font-mono">{i.number}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-black text-primary">₹{Number(i.total ?? i.estimated_total ?? i.amount ?? 0).toLocaleString()}</div>
                  <div className="label-overline mt-1">Level {i.approval_state?.current_level}/{i.approval_state?.total_levels}</div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">From {i.created_by_email} · {new Date(i.created_at).toLocaleDateString()}</div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => setTarget({ item: i, action: "APPROVE" })} data-testid={`approve-${i.id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" onClick={() => setTarget({ item: i, action: "REJECT" })} data-testid={`reject-${i.id}`}><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={() => { setTarget(null); setComment(""); }}>
        <DialogContent data-testid="approval-action-dialog">
          <DialogHeader><DialogTitle>{target?.action === "APPROVE" ? "Approve" : "Reject"} {target?.item.entity_type.replace("_", " ")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea placeholder="Optional comment…" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="approval-comment-input" />
            <Button onClick={act} data-testid="approval-confirm-btn">{target?.action === "APPROVE" ? "Confirm approval" : "Confirm rejection"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
